
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Contato() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  useEffect(() => {
    // Update page title and meta description
    document.title = `${t('contact.hero.title')} - Contact Us | Lions Trade Expert`;
    
    // Add JSON-LD structured data
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": t('contact.hero.title'),
      "description": t('contact.hero.subtitle'),
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/contato`,
      "mainEntity": {
        "@type": "Organization",
        "name": "Lions Trade Expert",
        "description": "Informative portal about sugar and coffee exports from Brazil to the world",
        "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}`,
        "contactPoint": {
          "@type": "ContactPoint",
          "contactType": "customer service",
          "availableLanguage": ["en", "pt", "es"]
        }
      },
      "about": {
        "@type": "Thing",
        "name": "Contact Information",
        "description": "Contact Lions Trade Expert for information about Brazilian commodity exports"
      },
      "keywords": "contact, Lions Trade Expert, commodity information, export consultation",
      "inLanguage": "en"
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [t]);

  const teamMembers = [
    {
      name: 'Dr. Carlos Silva',
      role: 'Especialista em Mercado de Commodities',
      expertise: 'Análise de mercado internacional e tendências de preços',
      icon: 'ri-user-line'
    },
    {
      name: 'Ana Rodrigues',
      role: 'Analista de Logística',
      expertise: 'Infraestrutura portuária e transporte',
      icon: 'ri-truck-line'
    },
    {
      name: 'Roberto Santos',
      role: 'Consultor em Agronegócio',
      expertise: 'Produção e exportação de café e açúcar',
      icon: 'ri-plant-line'
    }
  ];

  const dataSources = [
    { name: 'CONAB', description: 'Companhia Nacional de Abastecimento' },
    { name: 'IBGE', description: 'Instituto Brasileiro de Geografia e Estatística' },
    { name: 'MAPA', description: 'Ministério da Agricultura' },
    { name: 'CECAFÉ', description: 'Conselho dos Exportadores de Café' },
    { name: 'UNICA', description: 'União da Indústria de Cana-de-açúcar' },
    { name: 'FAO', description: 'Organização das Nações Unidas para Alimentação' }
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('sending');
    
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    
    // Validate textarea length
    const message = formData.get('mensagem') as string;
    if (message && message.length > 500) {
      alert('A mensagem deve ter no máximo 500 caracteres.');
      setFormStatus('idle');
      return;
    }
    
    try {
      await fetch('https://readdy.ai/api/form/d3c69afjefcg4ojr34rg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString()
      });
      
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/2ec165405065325596a07c08d459ec44.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              {t('contact.hero.title')}
            </h1>
            <h2 className="text-4xl md:text-5xl font-light mb-8 text-purple-300">
              {t('contact.hero.subtitle')}
            </h2>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-2xl"></i>
        </div>
      </section>

      {/* About Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent">
            {t('contact.about.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-purple-600 mx-auto mb-8"></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">{t('contact.about.title')}</h2>
              <div className="space-y-4 text-gray-700">
                <p>
                  Somos um projeto dedicado a fornecer informações precisas e atualizadas sobre 
                  o mercado de exportação de commodities brasileiras, com foco especial em açúcar e café.
                </p>
                <p>
                  Nossa missão é democratizar o acesso à informação sobre esses importantes setores 
                  da economia brasileira, servindo estudantes, pesquisadores, profissionais do 
                  agronegócio e todos os interessados no comércio exterior.
                </p>
                <p>
                  Utilizamos dados oficiais de fontes confiáveis para apresentar análises 
                  imparciais e educativas sobre os processos, mercados e tendências dessas commodities.
                </p>
              </div>
            </div>
            <div 
              className="h-80 rounded-lg"
              style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Professional%20Brazilian%20business%20team%20working%20with%20commodity%20market%20data%2C%20modern%20office%20environment%2C%20people%20analyzing%20agricultural%20export%20data%2C%20business%20meeting%2C%20professional%20photography%2C%20bright%20office%20lighting%2C%20clean%20composition&width=600&height=400&seq=team-work&orientation=landscape')`,
                backgroundSize: 'cover',
                backgroundPosition: 'object-top'
              }}
            ></div>
          </div>
        </div>
      </section>

      {/* Objectives Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent">
              {t('contact.objectives.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-purple-600 mx-auto mb-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-book-open-line text-green-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">{t('contact.objectives.educational.title')}</h3>
              <p className="text-gray-600">
                {t('contact.objectives.educational.description')}
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-information-line text-blue-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">{t('contact.objectives.informative.title')}</h3>
              <p className="text-gray-600">
                {t('contact.objectives.informative.description')}
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-global-line text-purple-600 text-2xl"></i>
              </div>
              <h3 className="text-xl font-bold mb-3">{t('contact.objectives.connective.title')}</h3>
              <p className="text-gray-600">
                {t('contact.objectives.connective.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent">
              {t('contact.team.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-purple-600 mx-auto mb-6"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${member.icon} text-gray-600 text-3xl`}></i>
                </div>
                <h3 className="text-xl font-bold mb-2">{member.name}</h3>
                <p className="text-green-600 font-medium mb-3">{member.role}</p>
                <p className="text-gray-600 text-sm">{member.expertise}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sources Section */}
      <section className="py-24 bg-gradient-to-r from-purple-900 via-purple-800 to-purple-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/10 to-indigo-500/10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
              {t('contact.sources.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-indigo-400 mx-auto mb-6"></div>
          </div>
          <p className="text-center text-gray-600 mb-8 max-w-3xl mx-auto">
            {t('contact.sources.description')}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dataSources.map((source, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                <h3 className="text-lg font-bold text-green-600 mb-2">{source.name}</h3>
                <p className="text-gray-600 text-sm">{source.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent">
              {t('contact.form.title')}
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-purple-600 mx-auto mb-8"></div>
          </div>
          <div className="text-center mb-12">
            <p className="text-xl text-gray-600">
              {t('contact.form.subtitle')}
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-lg p-8">
            <form 
              id="contact-form"
              data-readdy-form
              onSubmit={handleSubmit}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.name')} *
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder={t('contact.form.placeholder.name')}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('contact.form.email')} *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                    placeholder={t('contact.form.placeholder.email')}
                  />
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="assunto" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.form.subject')} *
                </label>
                <select
                  id="assunto"
                  name="assunto"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm pr-8"
                >
                  <option value="">{t('contact.form.selectSubject')}</option>
                  <option value="duvida">{t('contact.form.subjects.doubt')}</option>
                  <option value="sugestao">{t('contact.form.subjects.suggestion')}</option>
                  <option value="contribuicao">{t('contact.form.subjects.contribution')}</option>
                  <option value="parceria">{t('contact.form.subjects.partnership')}</option>
                  <option value="imprensa">{t('contact.form.subjects.press')}</option>
                  <option value="outro">{t('contact.form.subjects.other')}</option>
                </select>
              </div>

              <div className="mb-6">
                <label htmlFor="mensagem" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('contact.form.message')} *
                </label>
                <textarea
                  id="mensagem"
                  name="mensagem"
                  required
                  maxLength={500}
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm resize-none"
                  placeholder={t('contact.form.placeholder.message')}
                  onChange={(e) => {
                    const remaining = 500 - e.target.value.length;
                    const counter = document.getElementById('char-counter');
                    if (counter) {
                      counter.textContent = `${remaining} ${t('contact.form.charRemaining')}`;
                      counter.className = remaining < 50 ? 'text-red-500 text-xs' : 'text-gray-500 text-xs';
                    }
                  }}
                ></textarea>
                <div id="char-counter" className="text-gray-500 text-xs mt-1">500 {t('contact.form.charRemaining')}</div>
              </div>

              <div className="text-center">
                <button
                  type="submit"
                  disabled={formStatus === 'sending'}
                  className="bg-green-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-green-7..."
                >
                  {formStatus === 'sending' ? (
                    <>
                      <i className="ri-loader-line animate-spin mr-2"></i>
                      {t('contact.form.sending')}
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      {t('contact.form.send')}
                    </>
                  )}
                </button>
              </div>

              {formStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-center">
                  <i className="ri-check-line mr-2"></i>
                  {t('contact.form.success')}
                </div>
              )}

              {formStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-center">
                  <i className="ri-error-warning-line mr-2"></i>
                  {t('contact.form.error')}
                </div>
              )}
            </form>
          </div>
        </div>
      </section>

      {/* Social Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-5xl font-bold mb-6 bg-gradient-to-r from-purple-600 to-purple-800 bg-clip-text text-transparent">
            {t('contact.social.title')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-purple-600 mx-auto mb-8"></div>
          <p className="text-gray-300 mb-8">
            {t('contact.social.description')}
          </p>
          <div className="flex justify-center space-x-6">
            <a href="#" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
              <i className="ri-linkedin-line text-3xl"></i>
            </a>
            <a href="#" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
              <i className="ri-twitter-line text-3xl"></i>
            </a>
            <a href="#" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
              <i className="ri-facebook-line text-3xl"></i>
            </a>
            <a href="#" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
              <i className="ri-instagram-line text-3xl"></i>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
